import ServerErrorHandler from 'app/ErrorHandler';
import request from 'app/api/request';
import { useMutation, useQuery, useQueryClient } from 'react-query';

export const queryKeys = {
  themes: 'THEMES',
  themeDiff: 'THEME_DIFF',
  appliedStatus: 'THEME_APPLIED_STATUS',
};

export function useThemes(documentSuperId) {
  return useQuery(
    [queryKeys.themes, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/themes?documentSuperId=${documentSuperId}`);
      return res.data;
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useThemeDiff({ themeId, documentSuperId, enabled = true }) {
  return useQuery(
    [queryKeys.themeDiff, themeId, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/themes/${themeId}/diff?documentSuperId=${documentSuperId}`);
      return res.data;
    },
    {
      enabled: enabled && !!themeId && !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useAppliedThemeStatus(documentSuperId) {
  return useQuery(
    [queryKeys.appliedStatus, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/themes/applied-status?documentSuperId=${documentSuperId}`);
      return res.data;
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useCreateTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentSuperId, name }) => {
      const res = await request.post('/gaby/themes', { documentSuperId, name });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.themes);
        queryClient.invalidateQueries(queryKeys.appliedStatus);
        queryClient.invalidateQueries('document');
      },
      onError: ServerErrorHandler,
    }
  );
}

export function useRenameTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ themeId, documentSuperId, name }) => {
      const res = await request.patch(`/gaby/themes/${themeId}`, { documentSuperId, name });
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.themes),
      onError: ServerErrorHandler,
    }
  );
}

export function usePromoteTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ themeId }) => {
      const res = await request.post(`/gaby/themes/${themeId}/promote`, {});
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.themes),
      onError: ServerErrorHandler,
    }
  );
}

export function useDemoteTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ themeId, documentSuperId }) => {
      const res = await request.post(`/gaby/themes/${themeId}/demote`, { documentSuperId });
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.themes),
      onError: ServerErrorHandler,
    }
  );
}

export function useDeleteTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ themeId, documentSuperId }) => {
      const res = await request.del(`/gaby/themes/${themeId}?documentSuperId=${documentSuperId}`);
      return res.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.themes),
      onError: ServerErrorHandler,
    }
  );
}

export function useApplyTheme() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ themeId, documentSuperId, applyScope, conflictStrategy }) => {
      const res = await request.post(`/gaby/themes/${themeId}/apply`, { documentSuperId, applyScope, conflictStrategy });
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('draft');
        queryClient.invalidateQueries('document');
        queryClient.invalidateQueries('CSS');
        queryClient.invalidateQueries(queryKeys.appliedStatus);
      },
      onError: ServerErrorHandler,
    }
  );
}
